
<template>
  <div
    :key="projectId"
    class="project-detail-container"
  >
    <PageInfoHeader
      class="project-detail-header"
      :number="poNumber"
      :description="projectName"
      :shouldNavBack="pageInfoShouldNavBack"
      :status="currentProjectStatus"
    >
      <HKLHeader
        v-if="!isContractor"
        slot="header-top"
        :label-left-primary="projectHklLeftPrimaryLabel"
        link-class-left-primary="back"
        @left-primary="onProjectHklLeftPrimary"
        :label-right-leading="$t('listNavigation.previousRecord')"
        :label-right-trailing="$t('listNavigation.nextRecord')"
        :navigation-entries="projectListNavEntriesForHeader"
        :current-record-id="projectHeaderCurrentRecordId"
        :navigate-prev-enabled="isUnderBatch ? canGoToPreviousWorkPermitRecord : undefined"
        :navigate-next-enabled="isUnderBatch ? canGoToNextWorkPermitRecord : undefined"
        @right-leading="onProjectHklPrevious"
        @right-trailing="onProjectHklNext"
      />
      <div slot="buttons">
        <div class="row">
          <InviteBtn />
        </div>
      </div>
      <ProjectOptions slot="options" />
    </PageInfoHeader>

    <div class="project-detail-wrapper">
      <div
        v-if="canProjectAccess"
        class="project-po-info-wrapper"
      >
        <ul
          id="project-po-info"
          class="accordion accordion-customize"
          data-accordion
          data-deep-link="true"
          data-allow-all-closed="true"
          data-multi-expand="true"
          data-update-history="true"
          data-deep-link-smudge="500"
        >
          <li
            class="accordion-item is-active"
            data-accordion-item
          >
            <a class="accordion-title">
              <span>{{ $t('project.projectInfo') }}</span>
            </a>
            <div
              class="accordion-content"
              data-tab-content
            >
              <div class="project-info">
                <div class="row project-info-block">
                  <div class="small-12">
                    <label class="emphasis">{{ $t('project.purchaseOrder.vendorName') }}:</label>
                    <div v-if="showAdminVendorHover">
                      <p>
                        <HKLHoverTooltip>
                          <template #default>
                            <a :href="`mailto:${adminVendor.username}`">{{ vendorName }}</a>
                          </template>
                          <template #tips>
                            <div>{{ adminVendor.contacts.find((c) => c.type === 'NAME').value }}</div>
                            <a :href="`mailto:${adminVendor.username}`">{{ adminVendor.username }}</a>
                            <div>{{ adminVendor.contacts.find((c) => c.type === 'PHONE_NUMBER').value }}</div>
                            <div v-if="!adminVendor.enabled || !adminVendor.invitedAt">
                              {{ $t('project.purchaseOrder.vendorNameTooltips') }}
                            </div>
                          </template>
                        </HKLHoverTooltip>
                      </p>
                    </div>
                    <pre v-else><p>{{ vendorName }}</p></pre>
                  </div>

                  <div class="small-12">
                    <label class="emphasis">{{ $t('project.purchaseOrder.handledBy') }}:</label>
                    <template v-if="showHandleByHover">
                      <HoverTooltip
                        :content="hoverToolTipContent"
                        :url="hoverToolTipMail"
                        class="info-hover-tool-tip"
                      >
                        <div>
                          <a
                            :href="hoverToolTipMail"
                            target="_top"
                            style="text-decoration: none; color: black"
                          >
                            {{ currentProject.manager.username || '' }}
                          </a>
                        </div>
                      </HoverTooltip>
                    </template>
                    <template v-else>
                      <pre><p>{{ hoverToolTipContent }}</p></pre>
                    </template>
                  </div>
                  <div class="small-4 medium-4 large-4">
                    <label class="emphasis">{{ amountLabel }}:</label>
                    <pre><p>{{ purchase.amount | formatCurrency({currencyCode}) }}</p></pre>
                  </div>
                  <div
                    class="small-4 medium-4 large-4"
                  >
                    <template v-if="!isContractor || deduction.amount > 0">
                      <label class="emphasis">{{ $t('project.deduction.label') }}:</label>
                      <pre v-if="isContractor && !isProjectRepresentative"><p>{{ deduction.amount | formatCurrency({currencyCode}) }}</p></pre>
                      <p
                        v-else
                        id="add-deduction"
                        slot="activator"
                        class="add-deduction-link"
                      >
                        <a
                          :class="{'link-clickable': showDeductionLink, 'link-nonclickable': !showDeductionLink}"
                          @click="() => showDeductionLink && $refs.addDeductionModal.openModal()"
                        >
                          {{ deductionValue }}
                        </a>
                        <a
                          v-if="isPicView && isOpen"
                          class="fa fa-pencil icon"
                          @click="() => $refs.addDeductionModal.openModal()"
                        />
                      </p>
                    </template>
                  </div>
                  <div
                    class="small-4 medium-4 large-4"
                  >
                    <template v-if="deduction.amount > 0">
                      <label class="emphasis">{{ revisedLabel }}:</label>
                      <pre><p>{{ (purchase.amount - deduction.amount) | formatCurrency({force:true, currencyCode}) }}</p></pre>
                    </template>
                  </div>

                  <div class="small-12">
                    <label class="emphasis">{{ $t('project.purchaseOrder.tenderNumber') }}:</label>
                    <div
                      v-if="!prNumber"
                      class="tender-empty"
                    >
                      {{ $t('emptyValue') }}
                    </div>
                    <div v-if="prNumber">
                      <p>
                        <a
                          :class="{'link-clickable': showTender, 'link-nonclickable': !showTender}"
                          @click="() => showTender && navToTender()"
                        >
                          #{{ prNumber }}
                        </a>
                      </p>
                    </div>

                    <template v-if="tenderAdditionalItems.length">
                      <label class="emphasis additional-items">{{ $t('project.purchaseOrder.additionalItems') }}:</label>
                      <ul class="additional-items-list">
                        <li
                          v-for="item of tenderAdditionalItems"
                          :key="item.name"
                        >
                          {{ item.key }}: {{ item.value }}
                        </li>
                      </ul>
                    </template>
                  </div>
                  <div
                    class="small-12"
                  >
                    <label class="emphasis">{{ $t('project.relatedProject') }}:</label>
                    <div
                      v-if="isEditRelated"
                      ref="projectRelatedRef"
                      class="project-related"
                    >
                      <span
                        v-for="item in relatedProjects"
                        :key="item.id"
                      >
                        <a
                          class="related-link no-underline"
                        >
                          {{ getRelatedProjectsNumber(item) }}
                        </a>
                        <i
                          class="fa fa-times-thin"
                          @click="() => removeRelated(item)"
                        />
                      </span>

                      <input
                        id="related-project-number-input"
                        v-model="newPurchaseNumber"
                        type="text"
                        :placeholder="$t(`project.${numberType.toLowerCase()}.no`)"
                        class="related-project-number-input"
                        :class="{'error-highlight': isInvalidNumber}"
                      >
                      <input
                        v-show="numberType === PROJECT_TYPES.CONTRACT"
                        id="related-project-revision-input"
                        v-model="newPurchaseRevision"
                        type="text"
                        :placeholder="$t('revision')"
                        class="related-project-revision-input"
                        :class="{'error-highlight': isInvalidNumber}"
                      >
                      <i
                        class="fa fa-lg fa-plus"
                        @click="() => attachRelated()"
                      />
                    </div>
                    <div v-else>
                      <p>
                        <template v-if="deep(currentProject, 'relatedProjects.length')">
                          <template v-for="item in relatedProjects">
                            <router-link
                              v-if="!!item.relatedProjectId"
                              :key="item.id"
                              class="related-link"
                              :to="{ name: navToRouterName, params: { id: item.relatedProjectId, prev: currentRouteName } }"
                            >
                              {{ getRelatedProjectsNumber(item) }};
                            </router-link>
                            <template v-else>
                              {{ getRelatedProjectsNumber(item) }};
                            </template>
                          </template>
                        </template>
                        <template v-else>
                          {{ $t('emptyValue') }}
                        </template>
                        <a
                          v-if="isPicView && !isEditRelated"
                          id="modules-projects-detail-edit-related"
                          class="fa fa-pencil icon"
                          @click="() => enableEditRelated()"
                        />
                      </p>
                    </div>
                  </div>
                </div>

                <div class="row project-info-block">
                  <div class="small-2 medium-2 large-2">
                    <label class="emphasis">{{ $t('project.purchaseOrder.site') }}:</label>
                    <pre><p>{{ purchase.siteId }}</p></pre>
                  </div>
                  <div
                    v-if="buildingIncludedRequired"
                    ref="buildingIncludedRef"
                    class="small-10 medium-10 large-10"
                  >
                    <label class="emphasis">{{ $t('project.purchaseOrder.buildingIncluded') }}:</label>
                    <div
                      v-if="isEditBuildingIncluded"
                      class="building-included"
                    >
                      <HKLMultiSelect
                        :options="buildingHklOptions"
                        :value="selectedBuildingIds"
                        :respectOptionDisabled="true"
                        :showSearch="false"
                        optionLabelProp="label"
                        placeholder=""
                        @change="onBuildingIncludedChange"
                      />
                    </div>
                    <div v-else>
                      <p class="building-included-content">
                        {{ buildingIncludedDisplay }}
                        <a
                          v-if="showBuildingIncludedEditIcon"
                          class="fa fa-pencil icon"
                          @click="() => enableEditBuildingIncluded()"
                        />
                      </p>
                    </div>
                  </div>

                  <div
                    v-if="isOpen || retention.percentValue > 0"
                    class="small-12 project-retention-wrapper"
                  >
                    <label class="emphasis">{{ $t('project.retention.label') }}:</label>
                    <div
                      v-if="allowEditRetention && isEditRetention"
                      class="project-retention"
                    >
                      <input
                        id="project-retention-input"
                        v-model="retention.percentValue"
                        v-fusioninputcheck="{
                          type: 'integer',
                          pattern: (val) => {
                            const value = Number(val || '') || 0
                            return (value >= 0) && (value <= 100)
                          }
                        }"
                        type="text"
                        class="project-retention-input"
                        novalidate
                        autofocus
                        @focusout="() => updateRetention()"
                      ><span>%</span>
                    </div>
                    <div v-else>
                      <p @dblclick="() => editRetention()">
                        {{ (allowEditRetention && !retention.percentValue ? $t('emptyValue') : (retention.percentValue || '0') + '%') }}
                        <a
                          v-if="allowEditRetention"
                          id="modules-projects-detail-edit-retention"
                          class="fa fa-pencil icon"
                          @click="() => (isEditRetention = true)"
                        />
                      </p>
                    </div>
                    <div class="project-retention-amount">
                      {{ $t('equivalentTo') }} {{ retentionAmount | formatCurrency({force:true, currencyCode}) }}
                    </div>
                  </div>
                  <PencilEdit
                    class="small-12"
                    :fieldLabel="$t('project.actualCommDate.label')"
                    :displayValue="actualCommDateFieldTools.displayField.value"
                    :canEdit="actualCommDateFieldTools.canEdit.value"
                    @openPencil="actualCommDateFieldTools.initEditBoxValue"
                    @closePencil="actualCommDateFieldTools.updateFc"
                  >
                    <DatePicker
                      :calendarButton="true"
                      calendarButtonIcon="fa fa-calendar"
                      :value="formatDate(actualCommDateFieldTools.pendingField.value)"
                      @selected="val => actualCommDateFieldTools.setPendingField(val)"
                    />
                  </PencilEdit>

                  <PencilEdit
                    class="small-12"
                    :fieldLabel="$t('project.actualCompDate.label')"
                    :displayValue="actualCompDateFieldTools.displayField.value"
                    :canEdit="actualCompDateFieldTools.canEdit.value"
                    @openPencil="actualCompDateFieldTools.initEditBoxValue"
                    @closePencil="actualCompDateFieldTools.updateFc"
                  >
                    <DatePicker
                      :calendarButton="true"
                      calendarButtonIcon="fa fa-calendar"
                      :value="formatDate(actualCompDateFieldTools.pendingField.value)"
                      @selected="val => actualCompDateFieldTools.setPendingField(val)"
                    />
                  </PencilEdit>

                  <DlpDateField class="small-12" />
                </div>
              </div>

              <div class="row">
                <PencilEdit
                  class="small-12 project-remark"
                  :fieldLabel="$t('project.remarks.label')"
                  :canEdit="remarksFieldTools.canEdit.value"
                  @openPencil="remarksFieldTools.initEditBoxValue"
                  @closePencil="remarksFieldTools.updateFc"
                >
                  <TextField
                    :dirtyCheck="false"
                    :value="remarksFieldTools.pendingField.value"
                    @inputValue="remarksFieldTools.setPendingField"
                  />
                  <pre slot="display-show">{{ remarksFieldTools.displayField.value }}</pre>
                </PencilEdit>
              </div>
            </div>
          </li>
        </ul>
      </div>
      <ProjectRepresentative v-if="showProjectRepTable" />

      <div
        v-if="canProjectAccess"
        class="project-po-attachment-wrapper"
      >
        <ul
          id="project-po-attachment"
          class="accordion accordion-customize"
          data-accordion
          data-deep-link="true"
          data-allow-all-closed="true"
          data-multi-expand="true"
          data-update-history="true"
          data-deep-link-smudge="500"
        >
          <li
            class="accordion-item is-active"
            data-accordion-item
          >
            <a class="accordion-title"><span>{{ $t('project.projectDocs') }}</span></a>
            <div
              class="accordion-content"
              data-tab-content
            >
              <AttachedPo
                v-show="tenderDerived || poResourceId"
                :isTender="false"
                :purchase="purchase"
                :numberType="numberType"
                :purchaseNumbers="[poNumber]"
                :resourceIdMap="resourceIdMap"
                :awardedDate="awardedDate"
              />
              <div>
                <div
                  v-if="isEditDesc"
                  class="project-docs-desc"
                >
                  <textarea
                    id="project-guideline-docs-desc"
                    ref="docDesc"
                    v-model="guidelineDesc"
                    v-autosize
                    type="text"
                    class="autosize"
                    autofocus
                    @focusout="() => updateDescription()"
                  />
                  <div class="project-docs-desc-hint">
                    <popover
                      :text="$t('project.projectDocsHint')"
                      position="bottom-left"
                      :width="500"
                    />
                  </div>
                </div>
                <div v-else>
                  <pre
                    v-if="isPicView || guidelineDesc"
                    @dblclick="dblClickEdit()"
                  >{{ guidelineDesc || (isPicView && isOpen ? $t('project.docs.fillDescHint') : '') }}</pre>
                  <i
                    v-if="isPicView && isOpen"
                    class="fa fa-pencil icon"
                    @click="dblClickEdit"
                  />
                </div>
              </div>
              <HkldDropzone
                v-if="isCompleted ? isPicView : (isPicView && isOpen)"
                :sourceId="projectId"
                :action="getCreateDocAction()"
              />
              <EditAttachments
                :sourceId="projectId"
                :canEdit="isCompleted ? isPicView : (isPicView && isOpen)"
                :canDelete="isPicView && isOpen"
                :canDownloadAll="isProjectRepresentative || (isContractor && !showInviteBtn)"
                :action="getDeleteDocAction()"
                :zipName="`${currentProject.number}_Project_Documents.zip`"
              />
            </div>
          </li>
        </ul>
      </div>
      <div class="tabs-container-wrapper">
        <TabsContainer
          :key="projectId"
          ref="tabs-container"
          :tabNames="tabNames"
          class="tabs-container"
        >
          <template
            v-if="isUnderBatch"
            slot="float-right"
          >
            <i
              class="batch-review-nav__link show-only-while-sticky-header-on"
              :class="{ 'batch-review-nav__link--disabled': !canGoToPreviousWorkPermitRecord }"
              @click="toPreviousWorkPermit"
            >
              &lt; {{ $t('listNavigation.previousRecord') }}
            </i>
            <i
              class="batch-review-nav__link show-only-while-sticky-header-on"
              :class="{ 'batch-review-nav__link--disabled': !canGoToNextWorkPermitRecord }"
              @click="toNextWorkPermit"
            >
              {{ $t('listNavigation.nextRecord') }} &gt;
            </i>
          </template>
          <div
            v-if="showDocIconForUser"
            slot="icon-1"
            class="tab-new-doc"
          >
            {{ $t('tenantProjects.docs') }}
          </div>
          <SubmissionAccordion slot="tab-0" />
          <Documents slot="tab-1" />
          <ContractorInfo
            v-if="enableWorkPermitsFeature"
            slot="tab-2"
          />
          <WorkPermitView
            v-if="enableWorkPermitsFeature"
            ref="work-permit-view"
            slot="tab-3"
          />
        </TabsContainer>
      </div>
    </div>
    <add-deduction-modal
      ref="addDeductionModal"
      :editable="isPicView && isOpen"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { getCurrentInstance, ref, computed, unref } from '@vue/composition-api'
import { onClickOutside } from '@vueuse/core'
import router from '@/router'
import AddDeductionModal from '@/modules/projects/AddDeductionModal'
import AttachedPo from '@/modules/projects/AttachedPo'
import ContractorInfo from '@/modules/tenantProjects/contractors/ContractorInfo'
import Documents from '@/modules/projects/documents/Documents'
import EditAttachments from '@/controls/EditAttachments'
import HkldDropzone from '@/controls/HkldDropzone'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import HKLHeader from '@/controls/HKLHeader.vue'
import PageInfoHeader from '@/modules/PageInfoHeader'
import Popover from '@/controls/Popover'
import ProjectOptions from '@/modules/projects/ProjectOptions'
import SubmissionAccordion from '@/modules/projects/submission-request/SubmissionAccordion'
import TabsContainer from '@/controls/TabsContainer'
import WorkPermitView from '@/modules/workPermits/WorkPermitView'
import store, { useStoreGetters } from '@/store'
import { ActionTypes, projectRouteNames } from '@/utils/consts'
import { aclHelper } from '@/utils/aclHelper'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { getKey } from '@/api/common/request'
import { PO_MIN_LENGTH, PROJECT_TYPES } from '@/modules/projects/consts'
import { folderStatus, projectStatus } from '@/utils/locale'
import { getPurchaseNumber } from '@/api/projects/util'
import {
  updateProject,
  updateRelatedPurchases,
} from '@/api/projects/request'
import dateUtil from '@/utils/dateUtil'
import EventBus from '@/utils/eventBus'
import mutationTypes from '@/store/mutation-types'
import navigation, { navToDetailPage, navBackRouters } from '@/utils/navigation'
import notification from '@/utils/notification'
import userHelper from '@/utils/userHelper'
import HoverTooltip from '@/controls/HoverTooltip'
import TextField from '@/controls/TextField'
import DatePicker from '@/controls/DatePicker'
import PencilEdit from '@/controls/PencilEdit'
import { utils as formUtils } from '@/plugin/InputChange'
import { createContract, createPurchaseOrder, getExternalContract, getExternalPO } from '@/api/purchases/request'
import { showDocIcon } from '@/utils/projectUtil'
import filters from 'filters/filters'
import HKLHoverTooltip from '@/controls/HKLHoverTooltip.vue'
import { getVendorDetailByIdAndSite } from '@/api/users/request'
import i18n from '@/utils/i18n'
import { LIST_NAV_KIND, mergeListNavigationHeaderPaths, pushListNavigationEntry, resolveNavigationKind } from '@/constants/listNavigation'
import { VIEW_ALL_RESUME_SEARCH_QUERY } from '@/constants/viewAllTableCache'
import DlpDateField from './DlpDateField'
import { projectFieldEdit, fieldTypes, isProjectPic, useIsProjectRepresentative, useCanProjectAccess } from './projectInfo'
import InviteBtn from './InviteBtn'
import ProjectRepresentative from './ProjectRepresentative.vue'

export default {
  components: {
    HKLHeader,
    ProjectRepresentative,
    ContractorInfo,
    InviteBtn,
    DlpDateField,
    Documents,
    EditAttachments,
    HkldDropzone,
    HKLMultiSelect,
    PageInfoHeader,
    ProjectOptions,
    AttachedPo,
    SubmissionAccordion,
    TabsContainer,
    AddDeductionModal,
    Popover,
    WorkPermitView,
    HoverTooltip,
    TextField,
    DatePicker,
    PencilEdit,
    HKLHoverTooltip,
  },
  provide: function () {
    return {
      getIsValidRetention: () => this.isValidRetention,
      getAttachmentId: () => this.attachmentId,
    }
  },
  async beforeRouteEnter (to, from, next) {
    // to make sure child component can use current project
    // load current project before the page render
    const project = await store['_actions']['projects/loadProject'][0](to.params.id)
    // Abort routing and stop loading by throwing Error
    const projectFound = project && project.id
    projectFound ? next() : next(new Error('Project not found!'))
  },
  /**
   * Same-instance param change (e.g. HKL Prev/Next). Do not use `$router.go(0)` — full reload clears Vuex `listNavigation`.
   */
  async beforeRouteUpdate (to, from, next) {
    if (String(to.params.id) === String(from.params.id)) {
      this.syncListNavigationSequenceWithRoute(this.routeWorkPermitRecordId(to))
      next()
      return
    }
    try {
      await this.loadProject(to.params.id)
      this.syncListNavigationSequenceWithRoute(this.routeWorkPermitRecordId(to) || to.params.id)
    } catch (err) {
      EventBus.emit('module_loading_stopLoading')
      next(err)
      return
    }
    /* Commit route first so `:key="projectId"` replaces DOM; initAccordion must run on the new tree (same as former `$router.go(0)` full reload). */
    next()
    await this.$nextTick()
    await this.$nextTick()
    try {
      await this.syncProjectDetailUiAfterLoad()
      this.initAccordion()
      EventBus.$emit('refresh:project-submission-request')
      EventBus.$emit('modules.projects.documents.folderUpdated')
    } catch (e) {
      console.error(e)
    }
  },
  setup () {
    let returnData = {}

    const remarksFieldTools = projectFieldEdit('remarks', [projectStatus.DRAFT, projectStatus.ACTIVE, projectStatus.COMPLETED], isProjectPic, fieldTypes.TEXT)
    const actualCommDateFieldTools = projectFieldEdit('actualCommDate', [projectStatus.DRAFT, projectStatus.ACTIVE], isProjectPic, fieldTypes.DATE)
    const actualCompDateFieldTools = projectFieldEdit('actualCompDate', [projectStatus.DRAFT, projectStatus.ACTIVE], isProjectPic, fieldTypes.DATE)
    const { proxy } = getCurrentInstance()

    const buildingIncludedRef = ref(null)
    const projectRelatedRef = ref(null)

    onClickOutside(buildingIncludedRef, () => proxy.isEditBuildingIncluded ? proxy.updateBuildingIncluded() : '')
    onClickOutside(projectRelatedRef, () => {
      proxy.isEditRelated && proxy.updateRelatedProjects()
      formUtils.resetDirty()
    })
    const { currentProject } = useStoreGetters('projects', ['currentProject'])
    const currentProjectStatus = computed(() => {
      return i18n.t(`status.${unref(currentProject).status.toLowerCase()}`).toUpperCase()
    })
    returnData = {
      ...returnData,
      currentProject,
      currentProjectStatus,
      remarksFieldTools,
      actualCommDateFieldTools,
      actualCompDateFieldTools,
      buildingIncludedRef,
      projectRelatedRef,
    }

    const isProjectRepresentative = useIsProjectRepresentative()
    const canProjectAccess = useCanProjectAccess()
    returnData = {
      ...returnData,
      isProjectRepresentative,
      canProjectAccess,
    }

    const { isContractor, isHKlandUser } = useStoreGetters('user', ['isContractor', 'isHKlandUser'])
    const showAdminVendorHover = computed(() => {
      return proxy.adminVendor && (unref(isHKlandUser) || (unref(isContractor) && unref(isProjectRepresentative)))
    })
    const showHandleByHover = computed(() => {
      return unref(isContractor) && !unref(isProjectRepresentative)
    })
    const showProjectRepTable = computed(() => {
      return unref(isHKlandUser) || unref(isProjectRepresentative)
    })
    returnData = {
      ...returnData,
      showProjectRepTable,
      showAdminVendorHover,
      showHandleByHover,
    }

    return returnData
  },
  data () {
    return {
      showOptions: true,
      isEditBuildingIncluded: false,
      isEditDesc: false,
      isEditRetention: false,
      isEditRelated: false,
      retentionInvalid: false,
      isInvalidNumber: false,
      retention: {percentValue: ''},
      guidelineDesc: '',
      newPurchaseNumber: '',
      newPurchaseRevision: '',
      userHelper,
      cachedId: '',
      selectedBuildingIds: [],
      PROJECT_TYPES,
      adminVendor: null,
    }
  },

  computed: {
    ...mapGetters('projects', ['purchase', 'typeLabel', 'wpInUsedBuildingIds']),
    ...mapGetters('user', ['isContractor', 'currentUser', 'currentUserContact', 'isHKlandUser']),
    ...mapGetters('projectContractors', ['isMainUser']),
    ...mapGetters('workPermit', ['currentReviewWorkPermit', 'reviewQueue', 'isUnderBatch']),
    ...mapGetters('regions', ['enableWorkPermitsFeature']),
    ...mapGetters('buildings', ['allBuildings']),
    ...mapGetters('listNavigation', ['listNavigationSequence']),
    workPermitListNavRecordIndex () {
      if (!this.hasWorkPermitListNavigation) return -1
      const cur = this.workPermitHeaderCurrentRecordId
      return (this.listNavigationSequence || []).findIndex(e => String(e.id) === cur)
    },
    canGoToPreviousWorkPermitRecord () {
      return this.isUnderBatch && this.workPermitListNavRecordIndex > 0
    },
    canGoToNextWorkPermitRecord () {
      if (!this.isUnderBatch) return false
      const index = this.workPermitListNavRecordIndex
      const seq = this.listNavigationSequence || []
      if (index >= 0) return index < seq.length - 1
      return (this.reviewQueue || []).length > 0
    },
    previousWorkPermitListNavEntry () {
      const index = this.workPermitListNavRecordIndex
      if (index <= 0) return null
      return this.listNavigationSequence[index - 1]
    },
    nextWorkPermitListNavEntry () {
      const index = this.workPermitListNavRecordIndex
      const seq = this.listNavigationSequence || []
      if (index < 0 || index >= seq.length - 1) return null
      return seq[index + 1]
    },
    tabNames () {
      let result = [
        this.$i18n.t('project.submission.name'),
        this.$i18n.t('project.docs.name'),
      ]
      if (this.enableWorkPermitsFeature) result.splice(3, 0, this.$i18n.t('tenantProjects.contractorInfo'), this.$i18n.t('workPermit.title'))
      return result
    },
    deductionValue () {
      return this.deduction.amount ? filters.formatCurrency(this.deduction.amount, {currencyCode: this.currencyCode}) : this.$t('emptyValue')
    },
    showDeductionLink () {
      return ((!this.isPicView && !this.isContractor) || this.isProjectRepresentative) && this.deduction.amount
    },
    showDocIconForUser () {
      if (this.isPicView) return showDocIcon(this.currentProject)
      if (this.isProjectRepresentative) {
        const folders = this.currentProject.folders || []
        return folders.some(folder => {
          if (!(folder.docs || []).length) return false
          return [folderStatus.DRAFT, folderStatus.REJECTED].includes(folder.status)
        })
      }
      return false
    },
    currentRouteName () {
      return this.$router.currentRoute.name
    },
    navToRouterName () {
      return this.isContractor ? 'vendorProjectDetail' : 'projectDetail'
    },
    deduction () {
      return lodash.get(this.currentProject, 'deduction') || {}
    },
    currencyCode () {
      return lodash.get(this.purchase, 'currencyCode')
    },
    retentionAmount () {
      return lodash.get(this.retention, 'percentValue') * (this.purchase.amount - this.deduction.amount) / 100
    },

    allowEditRetention () {
      return this.isPicView && this.showInviteBtn
    },

    submissions () {
      return lodash.get(this.currentProject, 'submissions') || []
    },

    projectName () {
      return lodash.get(this.purchase, 'description')
    },

    vendorName () {
      return deep(this.purchase, 'company.name')
    },
    isDraft () {
      return deep(this.currentProject, 'status') === projectStatus.DRAFT
    },
    isActive () {
      return deep(this.currentProject, 'status') === projectStatus.ACTIVE
    },
    showInviteBtn () {
      return this.isDraft
    },
    isOpen () {
      return [projectStatus.DRAFT, projectStatus.ACTIVE].indexOf(deep(this.currentProject, 'status')) >= 0
    },
    isCompleted () {
      return [projectStatus.COMPLETED].indexOf(deep(this.currentProject, 'status')) >= 0
    },
    isPicView () {
      return aclHelper.isProjectPic()
    },
    showTender () {
      return this.isHKlandUser || this.isContractor
    },
    projectId () {
      return this.$route.params.id
    },
    poResourceId () {
      const projectAttachmentId = deep(this.currentProject, 'poAttachmentId')
      if (projectAttachmentId) {
        return projectAttachmentId
      }
      const tenderAttachmentMap = deep(this.purchase, ['tenders', 0, 'analysis', 'data', 'poResourceIds'])
      if (tenderAttachmentMap) {
        return tenderAttachmentMap[this.poNumber]
      }
      return null
    },
    tenderAdditionalItems () {
      if (!this.currentTender || !this.currentTender.optionalItems) return []
      return this.currentTender.optionalItems
        .filter((item) => item.selected)
        .map((item) => ({
          key: item.name,
          value: `${item.value} ${item.text}`,
        }))
    },
    prNumber () {
      return deep(this.currentTender, ['prNumber'])
    },
    currentTender () {
      return deep(this.purchase, ['tenders', 0])
    },
    tenderDerived () {
      return this.currentTender && Object.keys(this.currentTender).length > 0
    },
    poNumber () {
      return this.numberType === PROJECT_TYPES.PO
        ? lodash.get(this.purchase, 'number') : `${lodash.get(this.purchase, 'number')}-${lodash.get(this.purchase, 'revisionNum')}`
    },
    relatedProjects () {
      return (this.currentProject.relatedProjects || []).filter(item => !item.isDel)
    },
    numberType () {
      return lodash.get(this.currentProject, 'numberType')
    },
    attachmentId () {
      return this.poResourceId || this.cachedId
    },
    resourceIdMap () {
      return {
        [this.poNumber]: this.attachmentId,
      }
    },
    awardedDate () {
      const date = deep(this.currentTender, 'awardedDate')
      return date ? dateUtil.format(date, 'DD MMM YY HH:mm') : null
    },
    amountLabel () {
      return this.$t(`project.purchase.amount`, {type: this.typeLabel})
    },

    revisedLabel () {
      return `${this.$t('revised')} ${this.amountLabel}`
    },
    hasBuildingIncluded () {
      return this.currentProject.buildings && this.currentProject.buildings.length > 0
    },
    showBuildingIncludedEditIcon () {
      return this.isPicView && !this.isEditBuildingIncluded && (this.isDraft || this.isActive)
    },
    buildingIncludedRequired () {
      return [projectStatus.DRAFT, projectStatus.ACTIVE].includes(this.currentProject.status) && this.currentProject.site.buildings && this.currentProject.site.buildings.length > 0
    },
    buildingIncludedDisplay () {
      return this.hasBuildingIncluded
        ? this.currentProject.buildings.map(building => building.code).join(',')
        : this.$t('emptyValue')
    },
    buildingOptions () {
      const buildingList = lodash.cloneDeep(this.allBuildings)
      return buildingList.map(building => {
        building.removeDisabled = this.wpInUsedBuildingIds.includes(building.id)
        return building
      }) || []
    },
    buildingHklOptions () {
      return (this.buildingOptions || []).map((building) => {
        const option = {
          name: building.fullName,
          value: building.id,
          hklSelectSelectedLabel: building.code,
        }
        if (building.removeDisabled) {
          option.removeDisabled = true
        }
        return option
      })
    },
    hoverToolTipContent () {
      return userHelper.getUserName((this.currentProject.manager || {}).username || '', (this.currentProject.manager || {}).displayName)
    },
    hoverToolTipMail () {
      return this.currentProject.manager.username ? 'mailto:' + this.currentProject.manager.username : 'javascript:void(0)'
    },
    isValidRetention () {
      return this.retention.percentValue.length > 0 && this.retention.percentValue >= 0
    },
    pageInfoShouldNavBack () {
      if (!this.isContractor) {
        return false
      }
      return navBackRouters.projects.includes(this.$router.currentRoute.params.prev)
    },
    /** Prev/next + “Back to search results” only after View All committed a PROJECT-kind sequence */
    hasProjectListNavigation () {
      if (this.isContractor) return false
      const seq = this.listNavigationSequence || []
      const cur = String(this.$route.params.id)
      if (!seq.length) return false
      const allProject = seq.every(e => resolveNavigationKind(e) === LIST_NAV_KIND.PROJECT)
      const containsCur = seq.some(e => String(e.id) === cur)
      return allProject && containsCur
    },
    hasWorkPermitListNavigation () {
      if (this.isContractor) return false
      const seq = this.listNavigationSequence || []
      if (!seq.length) return false
      const allWorkPermit = seq.every(e => resolveNavigationKind(e) === LIST_NAV_KIND.WORK_PERMIT)
      if (!allWorkPermit) return false
      if (this.isUnderBatch) return true
      const cur = this.workPermitHeaderCurrentRecordId
      return !!(cur && seq.some(e => String(e.id) === cur))
    },
    workPermitHeaderCurrentRecordId () {
      const q = this.$route.query || {}
      return String(q.hoistingId || q.wpId || '')
    },
    projectHeaderCurrentRecordId () {
      return this.hasWorkPermitListNavigation ? this.workPermitHeaderCurrentRecordId : this.$route.params.id
    },
    projectListNavEntriesForHeader () {
      return this.hasProjectListNavigation || this.hasWorkPermitListNavigation ? this.listNavigationSequence : []
    },
    /** For `@right-leading` / `@right-trailing` → `pushListNavigationEntry`. */
    projectListNavMergedPaths () {
      return mergeListNavigationHeaderPaths(this.projectRecordNavPathsByKind, '', '')
    },
    projectRecordNavPathsByKind () {
      return { [LIST_NAV_KIND.PROJECT]: '/projects' }
    },
    projectHklLeftPrimaryLabel () {
      if (this.hasWorkPermitListNavigation) {
        return this.$t('listNavigation.backToDashboard')
      }
      if (this.hasProjectListNavigation) {
        return this.$t('listNavigation.backToSearchResults')
      }
      return this.$t('back')
    },
  },
  watch: {
    projectId () {
      const keepListNav = this.isUnderBatch || this.hasWorkPermitListNavigation || this.hasProjectListNavigation
      if (!keepListNav) {
        this.$router.go(0)
      }
    },
  },
  async mounted () {
    this.$nextTick(() => this.initAccordion())
    await this.syncProjectDetailUiAfterLoad()
    this.$nextTick(() => {
      EventBus.$emit('refresh:project-submission-request')
      EventBus.$emit('modules.projects.documents.folderUpdated')
    })
    const tabIndex = lodash.get(this.$router, 'currentRoute.query.tabIndex')
    if (this.$refs['tabs-container'] && tabIndex) {
      EventBus.$emit('common-tabs-container-select-tab', `#tab-${tabIndex}`, tabIndex)
    }
    this.syncListNavigationSequenceWithRoute()
  },

  destroyed () {
    if (this.canProjectAccess) {
      if (this.poInfoAccordion) {
        this.poInfoAccordion.destroy()
      }
      if (this.poAttachmentAccordion) {
        this.poAttachmentAccordion.destroy()
      }
    }
  },

  methods: {
    ...mapActions('listNavigation', ['clearListNavigationSequence']),
    ...mapActions('projects', ['loadProject']),
    deep,
    ...mapMutations('projects', {
      updateProjectField: mutationTypes.UPDATE_PATH_FIELD,
    }),

    navToProjectsViewAll () {
      navigation.toPage(projectRouteNames.projects, {}, { [VIEW_ALL_RESUME_SEARCH_QUERY]: '1' })
    },
    navToWorkPermitDashboard () {
      navigation.toPage('workPermitDashboard')
    },
    onProjectHklLeftPrimary () {
      if (this.hasWorkPermitListNavigation) {
        this.navToWorkPermitDashboard()
      } else if (this.hasProjectListNavigation) {
        this.navToProjectsViewAll()
      } else {
        this.$router.goBack()
      }
    },
    onProjectListNavEntry (entry) {
      pushListNavigationEntry(this.$router, this.$route, this.projectListNavMergedPaths, entry)
    },
    onProjectHklPrevious (entry) {
      if (this.isUnderBatch) {
        this.toPreviousWorkPermit()
        return
      }
      if (entry) this.onProjectListNavEntry(entry)
    },
    onProjectHklNext (entry) {
      if (this.isUnderBatch) {
        this.toNextWorkPermit()
        return
      }
      if (entry) this.onProjectListNavEntry(entry)
    },
    routeWorkPermitRecordId (route) {
      const q = (route && route.query) || {}
      return q.hoistingId || q.wpId || ''
    },
    /** @param {string} [explicitId] Use during `beforeRouteUpdate` before `$route` commits */
    syncListNavigationSequenceWithRoute (explicitId) {
      const cur = explicitId != null && explicitId !== ''
        ? String(explicitId)
        : String(this.projectHeaderCurrentRecordId)
      const seq = this.listNavigationSequence || []
      if (seq.length && !seq.some(e => String(e.id) === cur)) {
        const allWorkPermit = seq.every(e => resolveNavigationKind(e) === LIST_NAV_KIND.WORK_PERMIT)
        if (!(this.isUnderBatch && allWorkPermit)) {
          this.clearListNavigationSequence()
        }
      }
    },
    getCreateDocAction () {
      return ActionTypes.projects.relatedDoc.create
    },
    getDeleteDocAction () {
      return ActionTypes.projects.relatedDoc.delete
    },
    getRelatedProjectsNumber (item) {
      return getPurchaseNumber(item.type, item)
    },
    formatDate (item) {
      return dateUtil.formatDate(item)
    },
    navToTender () {
      this.isContractor ? router.push({
        name: 'tenderBidView',
        params: {
          id: deep(this.purchase, 'bid.id'),
        },
        query: {prev: this.$router.currentRoute.name},
      })
        : navToDetailPage(this.currentTender, this.$router.currentRoute.name)
    },
    enableEditBuildingIncluded () {
      this.selectedBuildingIds = (this.currentProject.buildings || []).map((building) => building.id)
      this.isEditBuildingIncluded = true
    },
    onBuildingIncludedChange (ids) {
      const lockedIds = this.wpInUsedBuildingIds.filter((id) =>
        this.selectedBuildingIds.includes(id),
      )
      this.selectedBuildingIds = lodash.uniq([...(ids || []), ...lockedIds])
    },
    async updateBuildingIncluded () {
      this.isEditBuildingIncluded = false
      const selectedBuildings = this.selectedBuildingIds
        .map((id) => this.buildingOptions.find((building) => building.id === id))
        .filter(Boolean)
      const selectedIds = selectedBuildings.map(({ id }) => id)
      await updateProject(this.projectId, { buildingIncludedIds: selectedIds })
      this.updateProjectField({ path: ['buildings'], newValue: selectedBuildings })
    },
    enableEditRelated () {
      this.isEditRelated = true
    },
    disableEditRelated () {
      this.isEditRelated = false
      this.newPurchaseNumber = ''
      this.newPurchaseRevision = ''
    },
    removeRelated (item) {
      let relatedProjects = lodash.cloneDeep(this.currentProject.relatedProjects)
      const toRemove = relatedProjects.find(rec => rec.id === item.id)
      toRemove.isDel = true
      this.updateProjectField({ path: ['relatedProjects'], newValue: relatedProjects })
    },
    async attachRelated (isSave) {
      let purchase
      this.isInvalidNumber = false
      this.newPurchaseNumber = this.newPurchaseNumber.trim()
      this.newPurchaseRevision = this.newPurchaseRevision.trim()
      if (isSave && !this.newPurchaseNumber) {
        return
      }
      const messageType = this.numberType.toLowerCase()

      try {
        if (this.numberType === PROJECT_TYPES.PO) {
          if (this.newPurchaseNumber === this.currentProject.number) {
            this.isInvalidNumber = true
            notification.fireNotification(false, this.$t(`error.${messageType}.400`))
            return
          }
          if (!this.newPurchaseNumber || this.newPurchaseNumber.length < PO_MIN_LENGTH) {
            this.isInvalidNumber = true
            notification.fireNotification(false, this.$t(`error.${messageType}.400`))
            return
          }
          purchase = await getExternalPO(this.newPurchaseNumber)
        } else {
          if (this.newPurchaseNumber === this.currentProject.contract.number && this.newPurchaseRevision === this.currentProject.contract.revisionNum) {
            this.isInvalidNumber = true
            notification.fireNotification(false, this.$t(`error.${messageType}.400`))
            return
          }
          purchase = await getExternalContract(this.newPurchaseNumber, this.newPurchaseRevision)
        }
      } catch (err) {
        this.isInvalidNumber = true
        notification.handleResponseError(err, messageType)
        return
      }

      if (purchase) {
        if (purchase.vendor.id !== this.currentProject.vendorId) {
          this.isInvalidNumber = true
          if (this.numberType === PROJECT_TYPES.PO) {
            notification.fireNotification(false, this.$t('error.po.vendorNotMatched', [this.numberType]))
          } else {
            notification.fireNotification(false, this.$t('error.po.vendorNotMatched', [messageType]))
          }

          return
        }
        const indexOfExistingRP = this.currentProject.relatedProjects.findIndex((relatedItem) =>
          relatedItem.type === this.numberType &&
          relatedItem.number === purchase.number &&
          relatedItem.revisionNum === purchase.revisionNum)
        if (indexOfExistingRP < 0) {
          purchase.amount = Number(purchase.amount)
          purchase.amountBase = Number(purchase.amountBase)
          if (this.numberType === PROJECT_TYPES.PO) {
            await createPurchaseOrder(purchase)
          } else {
            await createContract(purchase)
          }
          let relatedProjects = lodash.cloneDeep(this.currentProject.relatedProjects)
          relatedProjects.push({
            type: this.numberType,
            number: purchase.number,
            revisionNum: purchase.revisionNum,
            projectId: this.projectId,
          })

          this.updateProjectField({ path: ['relatedProjects'], newValue: relatedProjects })
        }
        this.newPurchaseNumber = ''
        this.newPurchaseRevision = ''
      } else {
        this.isInvalidNumber = true
        notification.fireNotification(false, this.$t(`error.${messageType}.400`))
      }
    },
    async updateRelatedProjects () {
      await this.attachRelated(true)
      if (this.isInvalidNumber) return
      const result = await updateRelatedPurchases(this.projectId, this.currentProject.relatedProjects)
      this.updateProjectField({ path: ['relatedProjects'], newValue: result })
      this.disableEditRelated()
    },
    async updateDescription () {
      this.isEditDesc = false
      await updateProject(this.projectId, { guidelineDesc: this.guidelineDesc })
    },
    validateRetention () {
      fusionInputCheckUtil.validateAllFields('.project-retention')
      return fusionInputCheckUtil.isAllFieldsValid('.project-retention')
    },

    async updateRetention () {
      if (!this.validateRetention()) {
        return
      }
      this.isEditRetention = false
      if (this.retention.percentValue.length === 0) {
        this.fetchRetention()
        return
      }
      await updateProject(this.projectId, { retention: { percentValue: this.retention.percentValue } })
      this.updateProjectField({ path: ['retention', 'percentValue'], newValue: this.retention.percentValue })
    },

    dblClickEdit () {
      if (this.isPicView && this.isOpen) {
        this.isEditDesc = true
      }
    },

    editRetention () {
      if (this.allowEditRetention) {
        this.isEditRetention = true
      }
    },

    fetchRetention () {
      const { isFromTender, percentValue } = (this.currentProject || {}).retention
      this.retention.isFromTender = isFromTender
      this.retention.percentValue = percentValue === null ? '' : '' + percentValue
    },

    async generateKey () {
      return getKey()
    },

    toPreviousWorkPermit () {
      if (!this.canGoToPreviousWorkPermitRecord) return
      pushListNavigationEntry(this.$router, this.$route, this.projectListNavMergedPaths, this.previousWorkPermitListNavEntry)
    },

    async toNextWorkPermit () {
      if (!this.canGoToNextWorkPermitRecord) return
      if (this.nextWorkPermitListNavEntry) {
        pushListNavigationEntry(this.$router, this.$route, this.projectListNavMergedPaths, this.nextWorkPermitListNavEntry)
        return
      }
      const workPermitView = this.$refs['work-permit-view']
      if (workPermitView && await workPermitView.toNextWorkPermit()) return
      if (workPermitView) await workPermitView.advanceReviewQueueToNextWorkPermit()
    },

    initAccordion () {
      if (this.poInfoAccordion && Object.keys(this.poInfoAccordion).length) {
        this.poInfoAccordion.destroy()
        delete this.poInfoAccordion
      }
      if (!this.poInfoAccordion) {
        this.poInfoAccordion = new Foundation.Accordion($('#project-po-info'))
      }
      if (this.poAttachmentAccordion && Object.keys(this.poAttachmentAccordion).length) {
        this.poAttachmentAccordion.destroy()
        delete this.poAttachmentAccordion
      }

      if (!this.poAttachmentAccordion) {
        this.poAttachmentAccordion = new Foundation.Accordion($('#project-po-attachment'))
      }
    },

    /** Guideline / retention / attachment key / vendor cache — also required after Prev–Next (not only first mount). */
    async syncProjectDetailUiAfterLoad () {
      this.guidelineDesc = (this.currentProject || {}).guidelineDesc || ''
      this.fetchRetention()
      this.cachedId = await this.generateKey()
      getVendorDetailByIdAndSite(this.purchase.vendor.id, this.purchase.siteId).then(([_, __, vendorAdminUser]) => {
        this.adminVendor = vendorAdminUser
      })
    },

  },
}
</script>

<style lang="scss">
@import "~styles/newMultiselect";
</style>

<style lang="scss" scoped>
@import '~styles/project/style';
@import '~styles/accordion';

.project-detail-container {
  ::v-deep .page-head-extra-top .hkl-header {
    margin-bottom: 4px;
  }
}

#project-po-info, #project-po-attachment {
  .accordion-title {
    background: white;
  }
}
.project-detail-wrapper {

  #project-po-info, #project-po-attachment, .tabs-container {
    max-width: 1440px;
    margin: 0 auto;
  }

  .project-po-info-wrapper, .project-po-attachment-wrapper {
    background: white;
    .accordion-content {
      padding: 15px 24px;
      .attachments-container {
        margin-top: 0;
        padding-top: 0;
      }
    }
  }

  #project-po-info label {
    margin-top: 0;
  }

  .tabs-container-wrapper {
    background: white;
    .tabs-container {
      padding: 15px 24px;
    }
  }
}
.project-retention-wrapper {
  &>div{
    display: inline-block;
  }
}

.tab-new-doc{
  margin-left:10px;
  margin-top: -3px;
}

.project-retention {
  color: #0C3A58;
}

.project-retention-input {
  display: inline-block;
  width: 80px;
}

.project-retention-amount {
  color: #0C3A58;
  vertical-align: middle;
  margin-left: 8px;
  margin-top: -7px;
}

pre {
  display: inline;
}
.fa{
    margin-left: 5px;
}

.related-link {
  text-decoration: underline;
  color: #0C3A58;
  &.no-underline {
    cursor: unset;
    text-decoration: none;
  }
}

.project-related {
  display: inline-block;
  .fa-times-thin {
    cursor: pointer;
    margin-left: 0;
    margin-right: 5px;
    &:before {
      content: '\00d7';
      color:#888;
      font-size: 20px;
    }
  }

  .error-highlight {
    background-color: #f9ecea;
  }

  .fa.fa-lg.fa-plus {
    cursor: pointer;
    margin: 5px;
  }

  .related-project-number-input {
    width: 130px;
    display: inline-block;
  }
  .related-project-revision-input {
    width: 90px;
    display: inline-block;
  }
}

.project-documents-doc-header-font {
  padding: 0.85rem 0;
  color: #6e7a91;
}

#project-guideline-docs-desc {
  display: inline-block;
  margin-right:0;
}

.project-docs-desc-hint {
  display: inline-block;
  vertical-align: top;
  color: #6e7a91;
}

.link-clickable {
  color: #0C3A58;
  text-decoration: underline;
}

a.link-nonclickable {
  color: #0C3A58;
  cursor: default;
}

.building-included {
  display: flex;
  button {
    max-height: 40px;
    &.save {
      margin-left: 15px;
    }
    &.cancel {
      margin-left: 5px;
      margin-right: 20px;
    }
  }
}
.project-info {
  display: flex;
  align-items: flex-start;
}
.info-hover-tool-tip {
  display: inline-block;
  margin-bottom: 8px;
}
.additional-items {
  font-weight: normal;
}
.additional-items-list{
  margin-left: 12px;
  margin-bottom: 12px;
  li {
    color: #0C3A58;
  }
}
.tender-empty {
  margin-bottom: 6px;
}
.building-included-content {
  word-wrap: break-word;
}
.project-info-block {
  width: 50%;
}
.project-remark {
  ::v-deep .field__edit {
    width: 100%;
  }
}
</style>
